import type { GameStateManager } from '../game/GameState';
import type { GameData, AnomalyReceivedResult, GameSpeed } from '../game/types';
import { AudioManager } from '../audio/AudioManager';
import { getAnomalyDisplayName } from '../utils/AnomalyNameResolver';

export class AnomalyReceivedModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onClose: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if (this.container.classList.contains('hidden')) return;
      
      const target = e.target as HTMLElement;
      if (target.classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Enter' || event.code === 'Escape' || event.code === 'Space') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(result: AnomalyReceivedResult): void {
    if (!this.container.classList.contains('hidden')) {
      return; // Already showing
    }

    this.container.classList.remove('hidden');
    this.render(result);
    this.gameState.setPausedByModal(true);
    AudioManager.getInstance().playEffect();
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    if (this.container.classList.contains('hidden')) {
      return; // Already hidden
    }

    // CRITICAL: Dequeue FIRST, before anything else
    this.gameState.dequeueAnomalyReceived();

    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    // Defer onClose to next tick to prevent synchronous re-showing of same modal
    setTimeout(() => {
      this.onClose();
    }, 0);
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private render(result: AnomalyReceivedResult): void {
    const strings = this.gameData.uiStrings;
    const propertyColors = strings.propertyColors || {};
    
    const anomaly = this.gameState.getAnomalyInCell(result.cellId);
    const displayName = anomaly ? getAnomalyDisplayName(anomaly, this.gameData) : result.anomalyName;
    
    const propertySymbol = strings.propertySymbols?.[result.knownProperty.toLowerCase()] || result.knownProperty;
    const propertyName = strings.properties?.[result.knownProperty.toLowerCase()] || result.knownProperty;
    const propColor = propertyColors[result.knownProperty.toLowerCase()] || '#6bb0ff';
    
    const propertyDisplay = `
      <div class="property-item newly-revealed" style="border-color: ${propColor}; color: ${propColor};">
        <span class="property-symbol" style="color: ${propColor};">${propertySymbol}</span>
        <span class="property-name" style="color: ${propColor};">${propertyName}</span>
        <span class="new-badge">KNOWN</span>
      </div>
    `;

    const cellName = strings.locations?.[result.cellId] || result.cellId;

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content anomaly-received-modal" tabindex="0">
        <div class="modal-header">
          <h2>${strings.messages?.anomaly_received || 'Anomaly Received'}</h2>
          <button class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <div class="anomaly-received-info">
            <div class="completion-section">
              <h3>Report</h3>
              <div>Your research facility has received a new anomaly contained by the field teams.</div>
            </div>
            
            <div class="completion-section">
              <h3>Anomaly</h3>
              <div class="anomaly-details">
                <div><strong>Name:</strong> ${displayName}</div>
                <div><strong>Location:</strong> ${cellName}</div>
              </div>
            </div>
            
            <div class="completion-section">
              <h3>Civilian Report</h3>
              <div>Based on civilian report, we can deduct one of its properties:</div>
              <div class="properties-list" style="margin-top: 10px;">
                ${propertyDisplay}
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-confirm button-close">Close</button>
        </div>
      </div>
    `;

    // Add button listeners - use once:true to auto-remove after one click
    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide(), { once: true });
    }

    const footerButton = this.container.querySelector('.button-close') as HTMLElement;
    if (footerButton) {
      footerButton.addEventListener('click', () => this.hide(), { once: true });
    }
  }
}
